// Angular or Core
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
// Service
import { AppPermissionService } from '@core/services/app-permission.service';
import { CurrencyHttpService } from '@core/services/currency-http.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { BetLimitGroupDataService } from './services/bet-limit-group-data.service';
// Model
import { Pagination } from '@core/models/pagination.model';
import { BetLimitGroupRow, BetLimitProviderOption } from '@core/models/bet-limit-group.model';
// Third Party
import * as moment from 'moment-timezone';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

/**
 * AIO-81599 — Members 1.8 Bet Limit: the list of sport bet limit groups.
 *
 * Each provider's default group is pinned above the paginated rows. It holds no players and
 * cannot be deleted; its icons open a limits-only setup page.
 */
@Component({
  selector: 'kt-bet-limit',
  templateUrl: './bet-limit.component.html',
  styleUrls: ['./bet-limit.component.scss'],
})
export class BetLimitComponent implements OnInit, OnDestroy {

  form: FormGroup;

  pageSize: number;
  page = 1;
  maxSize = 5;
  pagination: Pagination;

  dropdown = {
    perPage: this.dropdownHttpService.perPage,
    currencies: JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies')),
    providers: [] as BetLimitProviderOption[],
    statuses: [{ id: 1, name: 'Active' }, { id: 0, name: 'Inactive' }],
    dateTypes: [{ id: 'created_at', name: 'Created Date' }, { id: 'updated_at', name: 'Updated Date' }],
  };

  ranges = this.transactionHttpService.ranges;
  dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;

  params = '';

  /** Provider defaults first, then the current page. */
  tableRows: BetLimitGroupRow[] = [];

  private readonly defaultSortingConfig = {
    'id': 'desc',
    'code': 'desc',
    'name': 'desc',
    'members_count': 'desc',
    'status': 'desc',
  };
  private readonly defaultSortingSelection = { 'sort_by': 'id', 'sort_order': 'desc' };
  private readonly defaultPageSize = 30;

  sortingConfig = { ...this.defaultSortingConfig };
  sortingSelection = { ...this.defaultSortingSelection };

  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  exportBtnLoading = false;
  dataLength: number;

  canCreate = false;
  canEdit = false;
  canDelete = false;
  canExport = false;

  messages$ = this.betLimitGroupDataService.messages$;

  @ViewChild('dateRangeInput') private dateRangeInput: ElementRef<HTMLInputElement>;

  private readonly defaultFilters = {
    group: null,
    game_provider_id: 'all',
    currency_id: 'all',
    username: null,
    status: 'all',
    date_type: 'created_at',
    defaultDate: null,
    start_datetime: null,
    end_datetime: null,
  };

  private subscriptions = new Subscription();

  constructor(
    private betLimitGroupDataService: BetLimitGroupDataService,
    private dropdownHttpService: DropdownHttpService,
    private transactionHttpService: TransactionHttpService,
    private currencyHttpService: CurrencyHttpService,
    private appPermissionService: AppPermissionService,
    private loadingBar: LoadingBarService,
    private router: Router,
  ) { }

  ngOnInit() {
    this.pageSize = this.defaultPageSize;
    this.formInit();
    this.setCurrencyDropdown();

    this.subscriptions.add(this.betLimitGroupDataService.getOptions().subscribe(providers => this.dropdown.providers = providers));

    this.subscriptions.add(this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canCreate = appPermissions.create_sport_bet_limit_group;
      this.canEdit = appPermissions.edit_sport_bet_limit_group;
      this.canDelete = appPermissions.delete_sport_bet_limit_group;
      this.canExport = appPermissions.export_sport_bet_limit_group_list;
    }));

    this.onSubmit(true);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onSortColumn(property: string) {
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        this.sortingConfig[key] = key === property ? (this.sortingConfig[key] === 'asc' ? 'desc' : 'asc') : 'desc';
      }
    }
    this.sortingSelection.sort_by = property;
    this.sortingSelection.sort_order = this.sortingConfig[property];
    this.onViewPageBy(this.page);
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = !clearSearch;
    this.page = 1;
    this.params = this.buildParams();
    this.onViewPageBy();
  }

  onClear() {
    this.clearBtnLoading = true;
    this.sortingConfig = { ...this.defaultSortingConfig };
    this.sortingSelection = { ...this.defaultSortingSelection };
    this.pageSize = this.defaultPageSize;
    this.form.reset(this.defaultFilters);
    this.clearDateInput();
    this.onSubmit(true);
  }

  onViewPageBy(page = 1) {
    this.loading = true;
    this.page = page;
    this.loadingBar.start();
    const params = this.params ? `&${this.params}` : '';

    this.subscriptions.add(
      this.betLimitGroupDataService.getList(`?page=${page}&perPage=${this.pageSize}&${this.sortingParam()}${params}`).subscribe(
        data => {
          this.tableRows = (data.defaults ?? []).concat(data.rows ?? []);
          this.pagination = data.paginations;
          this.dataLength = this.tableRows.length;
          this.stopLoading();
        },
        () => this.stopLoading()
      )
    );
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page);
  }

  /** step: 0 Group Info, 1 Bet Limits, 2 Players. Same tab, so Back returns to this filtered list. */
  onOpenSetup(row?: BetLimitGroupRow, step = 0) {
    const queryParams = row ? { id: row.id, step } : {};
    this.router.navigate(['/general/bet-limit/setup'], { queryParams });
  }

  onDelete(row: BetLimitGroupRow) {
    Swal.fire({
      title: 'Delete bet limit group',
      html: `<p>Delete <b>${row.code}</b> — ${this.escape(row.name)}?</p>`
        + (row.members_count
          ? `<p>Its <b>${row.members_count}</b> player(s) will use the provider default again.</p>`
          : '<p>This group has no players.</p>')
        + '<p class="m-0">This cannot be undone.</p>',
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Delete Group',
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      this.loading = true;
      this.subscriptions.add(
        this.betLimitGroupDataService.delete(row.id).subscribe(
          () => this.onViewPageBy(this.page),
          () => this.loading = false
        )
      );
    });
  }

  onExport() {
    this.exportBtnLoading = true;
    const params = this.params ? `&${this.params}` : '';
    this.subscriptions.add(
      this.betLimitGroupDataService.export(`?${this.sortingParam()}${params}`).subscribe(
        () => this.exportBtnLoading = false,
        () => this.exportBtnLoading = false
      )
    );
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_datetime: event.startDate !== null ? event.startDate._d : event.startDate,
        end_datetime: event.endDate !== null ? event.endDate._d : event.endDate
      });
    }
  }

  /** See pwa-members onClearDate(): the picker rewrites its input after a form-only clear. */
  onClearDate() {
    this.form.patchValue({ defaultDate: null, start_datetime: null, end_datetime: null });
    this.clearDateInput();
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  private buildParams(): string {
    const data = { ...this.form.value };

    if (data.start_datetime) {
      // The operator picks a range in their own timezone; the API compares UTC.
      data.start_datetime = moment(data.start_datetime).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
      data.end_datetime = moment(data.end_datetime).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
    } else {
      delete data.date_type;
    }
    delete data.defaultDate;

    return Object.keys(data)
      .filter(key => data[key] !== '' && data[key] !== null && data[key] !== undefined && data[key] !== 'all')
      .map(key => `${key}=${encodeURIComponent(data[key])}`)
      .join('&');
  }

  private sortingParam(): string {
    return Object.keys(this.sortingSelection).map(key => `${key}=${this.sortingSelection[key]}`).join('&');
  }

  private stopLoading() {
    this.loading = false;
    this.clearBtnLoading = false;
    this.searchBtnLoading = false;
    this.loadingBar.complete();
  }

  private setCurrencyDropdown() {
    if (this.dropdown.currencies.length === 0) {
      this.subscriptions.add(this.currencyHttpService.setCurrency().subscribe(res => this.dropdown.currencies = res));
    }
  }

  private clearDateInput(): void {
    if (this.dateRangeInput) {
      this.dateRangeInput.nativeElement.value = '';
    }
  }

  private escape(value: string): string {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  private formInit() {
    this.form = new FormGroup({
      group: new FormControl(this.defaultFilters.group),
      game_provider_id: new FormControl(this.defaultFilters.game_provider_id),
      currency_id: new FormControl(this.defaultFilters.currency_id),
      username: new FormControl(this.defaultFilters.username),
      status: new FormControl(this.defaultFilters.status),
      date_type: new FormControl(this.defaultFilters.date_type),
      defaultDate: new FormControl(this.defaultFilters.defaultDate),
      start_datetime: new FormControl(this.defaultFilters.start_datetime),
      end_datetime: new FormControl(this.defaultFilters.end_datetime),
    });
  }
}
